import { Link } from '@tanstack/react-router';
import { SaplingLogo } from '@/components/icons/SaplingLogo';
import { ThemeToggle } from './ThemeToggle';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { User, LogOut, Settings, CreditCard, LayoutDashboard } from 'lucide-react';

interface HeaderProps {
  className?: string;
  onLoginClick?: () => void;
  onSignupClick?: () => void;
  showAuthButtons?: boolean;
}

export function Header({
  className,
  onLoginClick,
  onSignupClick,
  showAuthButtons = true,
}: HeaderProps) {
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    // Let Supabase handle its own cleanup (clear localStorage, stop
    // refresh timer, fire SIGNED_OUT event). Timeout ensures we always
    // reload even if signOut hangs.
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2000));
    await Promise.race([signOut(), timeout]);
    // Hard reload destroys React tree, Supabase in-memory state, and
    // TanStack Query cache. Fresh load finds no tokens → landing page.
    window.location.replace('/');
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
        className
      )}
    >
      <div className="container flex h-16 items-center justify-between">
        {/* Logo - navigate to dashboard when logged in, landing page otherwise */}
        <Link to={user ? '/app/dashboard' : '/'} className="flex items-center">
          <SaplingLogo size="md" />
        </Link>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <ThemeToggle />

          {user ? (
            /* Logged in - show user dropdown */
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline max-w-[150px] truncate">
                    {user.user_metadata?.full_name || user.email?.split('@')[0]}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.user_metadata?.full_name || 'User'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/app" className="cursor-pointer">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/app/billing" className="cursor-pointer">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Billing
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/app/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-error-100">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            /* Not logged in - show auth buttons */
            showAuthButtons && (
              <>
                <Button
                  variant="ghost"
                  onClick={onLoginClick}
                  className="hidden sm:inline-flex"
                >
                  Log in
                </Button>
                <Button onClick={onSignupClick}>Get Started</Button>
              </>
            )
          )}
        </div>
      </div>
    </header>
  );
}
